import Platform from '../platform';
import Config from '../config';
import {
	getCurrentAppIdSelector,
	getCurrentCustomerIdSelector,
	getCurrentRestaurantIdSelector,
} from "../selectors";
import {getLastActions} from "managers/action-tracking.manager";
import {LOG_STORAGE_KEY} from "new-constants/common.constants";
import {
	createReduxEnhancer,
	captureException,
	ErrorBoundary as SentryErrorBoundary,
	init,
	setUser, setTag,
	captureMessage
} from '@sentry/react';
import {Integrations} from "@sentry/tracing";

const types = {
	requestError: 'requestError',
	navigationLog: 'navigationLog',
	appStart: 'appStart',
	appInstall: 'appInstall',
	jsError: 'jsError',
	info: 'info',
};

let _store = null;


const MAX_ENTRIES = 1000;
const ENTRY_LIMIT_MESSAGE = 'Log entry limit reached. Some entries are lost.';
const LOCAL_STORAGE_KEY = LOG_STORAGE_KEY + Config.BaseServerUrl;


function LogEntry(message, time, url, type) {
	this.id = new Date().getTime();
	this.time = time;
	this.url = url;
	this.message = typeof message === 'object' ? JSON.stringify(message) : message;
	this.type = type;
	this.phonegap = Platform.isNativeApp;
	this.appVersion = Config.getAppVersion();
	this.userAgent = Config.ua;
	this.screenResolution = Platform.windowResolution();
	this.isPWA = Platform.isPWA;
	this.date = new Date().toUTCString();
}

function getStackString(errorInfo) {
	var removeUrlsPat = /\((https?:\/\/[^\s]+)/g;

	var stack = errorInfo && errorInfo.stack ? errorInfo.stack : new Error().stack;
	var stackString = stack ? JSON.stringify(stack).replace(removeUrlsPat, '') : '';

	var data = '';

	if (stackString) {
		var splitOne = stackString.split(' at ');
		var splitTwo = stackString.split('@');

		if (splitOne.length > splitTwo.length) {
			data = splitOne;
		} else {
			data = splitTwo;
		}
	}

	data = data ? data.splice(1, 4) : '';

	return data ? data.join('<<<') : '';
}

function saveEntry(entry, toPrint) {

	entry.restaurantId = getCurrentRestaurantIdSelector(_store?.getState());

	if (toPrint || !Config.prodMode) {
		printToConsole(entry);
	}

	let entries = JSON.parse(localStorage?.getItem(LOCAL_STORAGE_KEY));
	if (entries) {
		if (entries.length > MAX_ENTRIES) {
			entries = [
				new LogEntry(ENTRY_LIMIT_MESSAGE),
				...entries.slice(entries.length - (MAX_ENTRIES - 1), entries.length),
				entry,
			];
		} else {
			entries = [...entries, entry];
		}
	} else {
		entries = [entry];
	}
	localStorage?.setItem(LOCAL_STORAGE_KEY, JSON.stringify(entries));
}

function printToConsole(entry) {
	if (!Config.prodMode || window.location.href.includes('localhost')) {
		let errorBg;
		switch (entry.type) {
			case types.requestError:
				errorBg = 'background: #ffda5a; color: #000';
				break;
			case types.jsError:
				errorBg = 'background: #ff853c; color: #000';
				break;
			case types.navigationLog:
				errorBg = 'background: #71ffaa; color: #000';
				break;
			case types.info:
				errorBg = 'background: #FFFF99; color: #000';
				break;
			default:
				errorBg = 'background: #fbfa85; color: #000';
				break;
		}

		if (entry.type !== types.jsError) {
			return;
		}

		if (Platform.isNativeApp) {
			console.log(
				'%c> > > > Type: ' +
				entry.type +
				' | Message: ' +
				entry.message +
				' | Time: ' +
				entry.time +
				' | Url: ' +
				entry.url +
				' | Date: ' +
				entry.date +
				' | App: ' +
				entry.appVersion +
				' | Phonegap: ' +
				entry.phonegap +
				' | UserAgent: ' +
				entry.userAgent,
				errorBg
			);
		} else {
			console.group(['LOG:', entry.type, entry.url].join(' '));
			console.log(entry);
			console.groupEnd();
		}
	}
}


export const removeLogs = function removeLogs(idsToRemove) {
	const serializedLogs = localStorage?.getItem(LOCAL_STORAGE_KEY);
	if (!serializedLogs) {
		console.error('No logs to remove');
		return;
	}

	const currentLog = JSON.parse(serializedLogs);

	if (!currentLog) {
		console.error('Cannot parse logs to remove');
		return;
	}

	localStorage?.setItem(LOCAL_STORAGE_KEY, JSON.stringify(currentLog.filter(entry => !idsToRemove.includes(entry.id))));
}

export const setGlobalJsError = function setGlobalJsError(error) {
	captureException(error);
}

export const getLoggingReduxEnhancer = function getLoggingReduxEnhancer() {
	return createReduxEnhancer({});
}

export const setJsError = function setJsError(errorMsg, errorInfo) {
	captureException(errorMsg);
}

export const initErrorLogging = function initErrorLogging() {
	if (Config.sentryDsn?.length === 0) {
		return;
	}
	init({
		dsn: Config.sentryDsn,
		release: Config.getAppVersion() + ' ' + Config.getCommitHash(),
		integrations: [new Integrations.BrowserTracing()],
	});
}

export const subscribeErrorLogging = function subscribeErrorLogging(store) {

	_store = store;

	let currentTagsHash = null;

	store.subscribe(() => {
		const state = store.getState();

		const appId = getCurrentAppIdSelector(state);
		const customerId = getCurrentCustomerIdSelector(state);
		const restaurantId = getCurrentRestaurantIdSelector(state);

		const newTagsHash = `${appId} ${customerId} ${restaurantId}`;

		if (newTagsHash !== currentTagsHash) {
			currentTagsHash = newTagsHash;
			setUser({id: customerId});
			setTag('custId', customerId);
			setTag('restaurantId', restaurantId);
			setTag('appId', appId);
			setTag('platform', Platform.isPWA ? 'pwa' : 'web');
		}
	});
};

export const setApplicationStartMessage = function setApplicationStartMessage() {
	const entry = new LogEntry(null, null, window.location.href, types.appStart);
	// Temporarily disable
	//saveEntry(entry, true);
}

const RestaurantIdsWithLogging = [22206, 22207, 22208, 22235, 23706, 22410, 22411, 23229, 23581, 23293, 22052, 21901];

export const setInfoMessage = function setInfoMessage(message) {
	const entry = new LogEntry(message, null, window.location.href, types.info);
	// Temporarily disable
	//saveEntry(entry, false);

	// Enable for one restaurant for debugging
	if(!_store) return;

	const restaurantId = getCurrentRestaurantIdSelector(_store.getState());
	if(!restaurantId) return;

	if(RestaurantIdsWithLogging.includes(restaurantId)){
		captureMessage(typeof message === 'object' ? JSON.stringify(message) : message);
	}
}

export const setRequestErrorLog = function setRequestErrorLog(requestUrl, errorMessage) {
	const entry = new LogEntry(errorMessage, null, requestUrl, types.requestError);
	// Temporarily disable
	//saveEntry(entry, true);
}

export const ClientLogErrorBoundary = SentryErrorBoundary;
